/**
 * Pure aggregation for the internal metrics dashboard.
 *
 * No React, no fetching, no dates-from-now — every input is an argument, so
 * this file is fully testable and can be swapped for a server-side aggregate
 * later by changing only its caller.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * TWO PROVENANCE CONVENTIONS LIVE HERE. They are NOT interchangeable.
 *
 * v1 columns (original_matter, original_description, original_units,
 * original_task, original_activity) hold the ORIGINAL value and are compared
 * against the current one:
 *     corrected  <=>  original !== null && original !== current
 * This matches AdminEntryDetailDialog.tsx:140.
 *
 * v2 columns (originalEntryGroupId, originalMatterId) are written ONCE on the
 * attorney's FIRST EDIT and hold the value that was replaced. Presence is the
 * whole signal:
 *     corrected  <=>  original !== null
 * Do NOT compare them against the current value: the comparison is
 * non-indexable upstream and drops rows when either side is null.
 * ─────────────────────────────────────────────────────────────────────────
 */

import {
	CORRECTION_FIELDS,
	METRIC_STATUSES,
	type CorrectionField,
	type MatterProvenance,
	type Measure,
	type MetricsBucket,
	type MetricsRow,
	type MetricsSummary,
	type StatusTotals,
} from "./types";

// ─────────────────────────────────────────────
// v1 field corrections (compare original vs current)
// ─────────────────────────────────────────────

/** True when a v1 original_* field records a real change. */
function changed<T>(original: T | null | undefined, current: T): boolean {
	return original !== null && original !== undefined && original !== current;
}

// ─────────────────────────────────────────────
// v2 provenance (presence is the signal)
// ─────────────────────────────────────────────

/**
 * Classify the group-matter provenance column.
 *
 * The empty string is a real, meaningful value — the system produced no matter
 * and the attorney supplied one. It must never be collapsed into "untouched".
 */
export function matterProvenanceOf(
	originalMatterId: string | null | undefined,
): MatterProvenance {
	if (originalMatterId === null || originalMatterId === undefined) {
		return "untouched";
	}
	if (originalMatterId === "") return "system_missed";
	return "system_wrong";
}

/**
 * True when a human moved this entry between groups.
 *
 * The ingest grouper re-places entries automatically on re-ingest and
 * deliberately does not write this column, so the signal carries no system
 * noise and "attorney regrouped this" is an accurate label.
 */
export function wasRegrouped(row: MetricsRow): boolean {
	return (
		row.originalEntryGroupId !== null && row.originalEntryGroupId !== undefined
	);
}

/** Which v1 original_* values this row actually carries, by correction field. */
function v1ValuesPresent(row: MetricsRow): Record<CorrectionField, boolean> {
	return {
		matter: row.originalMatter !== null,
		narrative: row.originalDescription !== null,
		units: row.originalUnits !== null,
		task: row.originalTask !== null,
		activity: row.originalActivity !== null,
		// v2 fields track coverage at the family level, not here.
		groupMatter: false,
		groupMembership: false,
	};
}

/** True when the row carries the v2 provenance fields at all. */
function hasProvenanceFields(row: MetricsRow): boolean {
	return (
		row.originalEntryGroupId !== undefined || row.originalMatterId !== undefined
	);
}

/** Per-field correction flags for one row. */
export function correctionsOf(
	row: MetricsRow,
): Record<CorrectionField, boolean> {
	return {
		matter: changed(row.originalMatter, row.matterId),
		narrative: changed(row.originalDescription, row.description),
		units: changed(row.originalUnits, row.units),
		task: changed(row.originalTask, row.task),
		activity: changed(row.originalActivity, row.activity),
		groupMatter: matterProvenanceOf(row.originalMatterId) !== "untouched",
		groupMembership: wasRegrouped(row),
	};
}


// ─────────────────────────────────────────────
// Capture source
//
// NOTE ON THE DATA: /admin/user-transactions is a v1-SHAPED FACADE OVER V2.
// It returns ONE ROW PER GROUP (time_entry_groups), and `app_name` is the
// application of that group's FIRST entry only — so a group mixing a manual
// entry with captured ones is classified by whichever entry sorts earliest.
// Treat these predicates as group-level approximations, not entry truth.
// ─────────────────────────────────────────────

/** Hand-created entries carry this application marker. */
export const MANUAL_APPLICATION = "User Input";

export function isManual(row: MetricsRow): boolean {
	return row.application === MANUAL_APPLICATION;
}

/**
 * Groups we cannot classify, and therefore must not count on either side.
 *
 * A null application is the visible signature of a timer-fill entry: the timer
 * writes fill rows with `source = 'timer_fill'` and NO application_id, so the
 * join yields null. Those are system-created placeholders, not predictions —
 * counting them as "generated" inflates the denominator and flatters anyone
 * who leans on the timer.
 *
 * This is a proxy. The correct predicate is the entry's `source`, which this
 * endpoint does not expose; a null application could also be a legitimate
 * group whose first entry simply lacks an app. So these are excluded from both
 * numerator and denominator and counted separately, rather than silently
 * assigned to either side.
 */
export function isUnclassified(row: MetricsRow): boolean {
	return row.application === null;
}

/** True when Hourglass generated this group from captured activity. */
export function isGenerated(row: MetricsRow): boolean {
	return !isManual(row) && !isUnclassified(row);
}

// ─────────────────────────────────────────────
// Bucket construction
// ─────────────────────────────────────────────

function emptyTotals(): StatusTotals {
	return { count: 0, seconds: 0 };
}

function emptyCorrections(): Record<CorrectionField, number> {
	return {
		matter: 0,
		narrative: 0,
		units: 0,
		task: 0,
		activity: 0,
		groupMatter: 0,
		groupMembership: 0,
	};
}

export function emptyBucket(profId: string, profName: string): MetricsBucket {
	return {
		profId,
		profName,
		byStatus: {
			tracked: emptyTotals(),
			exported: emptyTotals(),
			deleted: emptyTotals(),
		},
		corrections: emptyCorrections(),
		matterProvenance: { untouched: 0, system_missed: 0, system_wrong: 0 },
		headline: {
			generated: 0,
			generatedSeconds: 0,
			generatedDeleted: 0,
			generatedDeletedSeconds: 0,
			exported: 0,
			exportedSeconds: 0,
			exportedManual: 0,
			exportedManualSeconds: 0,
			unclassified: 0,
			missingDuration: 0,
		},
		lastActive: null,
	};
}

function addRow(bucket: MetricsBucket, row: MetricsRow): void {
	// A row always counts; it contributes hours only when it has a duration.
	const seconds = row.seconds ?? 0;
	if (row.seconds === null) bucket.headline.missingDuration += 1;

	const status = bucket.byStatus[row.status];
	if (status) {
		status.count += 1;
		status.seconds += seconds;
	}

	if (!bucket.lastActive || row.date > bucket.lastActive) {
		bucket.lastActive = row.date;
	}

	if (isGenerated(row)) {
		bucket.headline.generated += 1;
		bucket.headline.generatedSeconds += seconds;
		if (row.status === "deleted") {
			bucket.headline.generatedDeleted += 1;
			bucket.headline.generatedDeletedSeconds += seconds;
		}
	}
	if (isUnclassified(row)) bucket.headline.unclassified += 1;
	if (row.status === "exported" && !isUnclassified(row)) {
		bucket.headline.exported += 1;
		bucket.headline.exportedSeconds += seconds;
		if (isManual(row)) {
			bucket.headline.exportedManual += 1;
			bucket.headline.exportedManualSeconds += seconds;
		}
	}

	for (const field of CORRECTION_FIELDS) {
		if (correctionsOf(row)[field]) bucket.corrections[field] += 1;
	}
	bucket.matterProvenance[matterProvenanceOf(row.originalMatterId)] += 1;
}

// ─────────────────────────────────────────────
// Public entry point
// ─────────────────────────────────────────────

export interface AggregateInput {
	rows: MetricsRow[];
	/**
	 * Every licensed timekeeper. Seeded up front so attorneys with zero
	 * activity render as explicit zero rows instead of vanishing — they are the
	 * most important signal on an adoption dashboard.
	 */
	timekeepers: Array<{ profId: string; profName: string }>;
}

export function aggregate({ rows, timekeepers }: AggregateInput): MetricsSummary {
	const firm = emptyBucket("__firm__", "All timekeepers");
	const buckets = new Map<string, MetricsBucket>();
	for (const tk of timekeepers) {
		buckets.set(tk.profId, emptyBucket(tk.profId, tk.profName));
	}

	let provenanceCoverage = 0;
	const v1ProvenanceCoverage = emptyCorrections();

	for (const row of rows) {
		// An entry from someone missing from the timekeeper list still counts —
		// dropping it would silently understate firm totals.
		let bucket = buckets.get(row.profId);
		if (!bucket) {
			bucket = emptyBucket(row.profId, row.profName);
			buckets.set(row.profId, bucket);
		}

		addRow(bucket, row);
		addRow(firm, row);

		if (hasProvenanceFields(row)) provenanceCoverage += 1;
		const present = v1ValuesPresent(row);
		for (const field of CORRECTION_FIELDS) {
			if (present[field]) v1ProvenanceCoverage[field] += 1;
		}
	}

	const perUser = [...buckets.values()].sort((a, b) =>
		a.profName.localeCompare(b.profName),
	);

	return { firm, perUser, provenanceCoverage, v1ProvenanceCoverage };
}

/**
 * Sum buckets into one, for the firm rollup when timekeepers are temporarily
 * excluded. The aggregator's own `firm` bucket covers everyone, so filtering
 * has to re-total from the per-user buckets rather than subtract from it.
 */
export function mergeBuckets(
	buckets: MetricsBucket[],
	label = "Included timekeepers",
): MetricsBucket {
	const out = emptyBucket("__merged__", label);

	for (const b of buckets) {
		for (const status of METRIC_STATUSES) {
			out.byStatus[status].count += b.byStatus[status].count;
			out.byStatus[status].seconds += b.byStatus[status].seconds;
		}
		for (const field of CORRECTION_FIELDS) {
			out.corrections[field] += b.corrections[field];
		}
		for (const key of ["untouched", "system_missed", "system_wrong"] as const) {
			out.matterProvenance[key] += b.matterProvenance[key];
		}
		for (const key of Object.keys(out.headline) as Array<
			keyof MetricsBucket["headline"]
		>) {
			out.headline[key] += b.headline[key];
		}
		if (b.lastActive && (!out.lastActive || b.lastActive > out.lastActive)) {
			out.lastActive = b.lastActive;
		}
	}

	return out;
}

// ─────────────────────────────────────────────
// Display helpers
// ─────────────────────────────────────────────

/** Formats a measure value for display; null renders as an em-dash. */
export function formatAmount(value: number | null, measure: Measure): string {
	if (value === null) return "—";
	return measure === "count"
		? Math.round(value).toLocaleString()
		: `${value.toFixed(1)}h`;
}

/** Safe rate helper — returns null rather than NaN or a misleading 0%. */
export function rate(numerator: number, denominator: number): number | null {
	if (denominator <= 0) return null;
	return (numerator / denominator) * 100;
}

export function measureOf(totals: StatusTotals, measure: Measure): number {
	return measure === "count" ? totals.count : totals.seconds / 3600;
}


export function formatRate(value: number | null): string {
	return value === null ? "—" : `${value.toFixed(1)}%`;
}

/** Everything captured, whatever became of it — the composition denominator. */
export function captured(bucket: MetricsBucket, measure: Measure): number {
	return (
		measureOf(bucket.byStatus.tracked, measure) +
		measureOf(bucket.byStatus.exported, measure) +
		measureOf(bucket.byStatus.deleted, measure)
	);
}


/** Deleted as a share of what Hourglass generated. */
export function deletionRate(bucket: MetricsBucket): number | null {
	return rate(bucket.headline.generatedDeleted, bucket.headline.generated);
}

/** Hand-created as a share of everything exported. */
export function manualRate(bucket: MetricsBucket): number | null {
	return rate(bucket.headline.exportedManual, bucket.headline.exported);
}
